import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';

import { t } from '@/utility/localization';

import InfoCircleIcon from '@/components/common/Icons/InfoCircleIcon';
import ToolTip from '@/components/common/ToolTip';
import Icon from '@/components/npl/Icon';

import useOutsideComponentClickDetect from '@/hooks/useOutsideComponentClickDetect';

const FilterDropdown = ({
  options = [],
  onChange = () => {},
  selected,
  title = t('filter'),
  customIcon,
  customClassNames,
  footerText,
  customLabel,
  placementRight = false
}) => {
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState(null);

  const handleClose = () => {
    setIsOpen(false);
    setActiveSubMenu(null);
  };

  const toggleDropdown = () => {
    if (activeSubMenu) {
      setActiveSubMenu(null);
    }
    setIsOpen((prev) => !prev);
  };
  const onOptionChange = ({ option }) => {
    onChange({ option });
    handleClose();
  };

  useOutsideComponentClickDetect(dropdownRef, () => handleClose());

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        role="button"
        tabIndex={0}
        className={`flex cursor-pointer items-center justify-center space-x-8 rounded-full border-1 border-npl-neutral-light-solid-7 px-12 py-6 hover:bg-npl-neutral-light-solid-3 ${customClassNames}`}
        onClick={toggleDropdown}>
        {customIcon && <Icon name={customIcon} height={16} width={16} />}
        <p className="ml-6 whitespace-nowrap text-14 font-medium text-npl-text-icon-on-light-surface-primary">
          {`${title ? title + ': ' : ''}${
            selected?.useCustomLabel
              ? customLabel || selected?.label
              : selected?.label
          }`}
        </p>
        <Icon
          name="chevron-down"
          className={`transition-transform ease-in-out ${
            isOpen ? 'rotate-180' : ''
          }`}
          width={16}
          height={16}
        />
      </div>
      {isOpen && (
        <div
          className={classNames(
            'absolute top-[40px] z-[101] min-w-[200px] cursor-pointer rounded-12 bg-white-default p-8 shadow-npl-styles-shadow-02',
            {
              'right-0': placementRight,
              'left-0': !placementRight
            }
          )}>
          {options?.map((option, index) => {
            const { label, value, tooltipText, subMenu } = option;
            const isSelected = selected?.value === value;
            const handleSubMenuToggle = () => {
              setActiveSubMenu(activeSubMenu === index ? null : index);
              setIsOpen(false);
            };
            return (
              <div
                role="button"
                tabIndex={0}
                key={index}
                onClick={() => {
                  if (subMenu) {
                    handleSubMenuToggle(); // Call the function to toggle the submenu
                  } else {
                    onOptionChange({ option });
                  }
                }}
                className={`flex flex-row items-center justify-between rounded-8 px-10 py-8 hover:bg-light-f2 active:bg-light-e2`}>
                <div className="flex flex-row">
                  <p className="mr-8 text-16 text-dark-33">{label}</p>
                  {tooltipText && (
                    <ToolTip
                      text={
                        <p className="min-w-[220px]">{tooltipText}</p>
                      }>
                      <InfoCircleIcon />
                    </ToolTip>
                  )}
                </div>
                {subMenu && (
                  <Icon
                    name="chevron-right"
                    fill="#1B1B1880"
                    height={16}
                    width={16}
                  />
                )}
                {isSelected && <Icon name="check" strokeColor="#1b1b1b" />}
              </div>
            );
          })}
          {footerText && (
            <>
              <div className="mx-12 my-4 h-[1px] bg-npl-neutral-light-solid-6" />
              <div className="px-12 py-12 text-label-sm text-npl-text-icon-on-light-surface-tertiary">
                {footerText}
              </div>
            </>
          )}
        </div>
      )}

      {activeSubMenu && (
        <div className="absolute left-0 right-0 top-[48px] -ml-80 cursor-pointer rounded-8 bg-white-default md:ml-0">
          {options[activeSubMenu]?.subMenu(handleClose)}
        </div>
      )}
    </div>
  );
};

FilterDropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      tooltipText: PropTypes.string
    })
  ),
  onChange: PropTypes.func,
  title: PropTypes.string
};

export default FilterDropdown;
